import type { Wallpaper } from '../types';
import {
  api,
  accountHeaders,
  throwResponseError,
  getAccountRevision,
  assertAccountRevision,
} from './api';
import { supabase, SUPABASE_CHECKOUT, SUPABASE_ENVIRONMENT } from './supabase';
export type DownloadFormat = 'png' | 'mp4';

function filename(item: Wallpaper, format: DownloadFormat) {
  return `MUJIAN-${item.id}-${format === 'png' ? '2160x4680' : '1080x2340'}.${format}`;
}

function saveBlob(blob: Blob, item: Wallpaper, format: DownloadFormat) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename(item, format);
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Prefer the Supabase-signed original for owned artworks. Pages Functions are
 * not deployed during the migration, so any 401/403/404 falls back to the
 * legacy binary endpoint. Once the signed URL is granted, a fetch failure is a
 * real error and must not silently retry the legacy path.
 */
async function downloadFromSupabase(
  item: Wallpaper,
  format: DownloadFormat,
): Promise<'done' | 'fallback'> {
  if (!supabase) return 'fallback';
  const session = await supabase.auth.getSession().catch(() => null);
  const token = session?.data.session?.access_token;
  let response: Response;
  try {
    response = await fetch(
      `/api/download/${encodeURIComponent(item.id)}?format=${format}&environment=${SUPABASE_ENVIRONMENT}`,
      {
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
  } catch {
    return 'fallback';
  }
  if (!response.ok) return 'fallback';
  const payload = (await response.json().catch(() => null)) as { url?: unknown } | null;
  if (!payload || typeof payload.url !== 'string') return 'fallback';
  const file = await fetch(payload.url).catch(() => null);
  if (!file?.ok) throw new Error('下载链接已失效，请稍后重试。');
  saveBlob(await file.blob(), item, format);
  return 'done';
}

/**
 * Claim a free artwork for the signed-in account or the current guest session.
 * The Pages Function is the target; a legacy server has no /api/claim route, so
 * 404/405/501 returns 'legacy' and the caller keeps using the old checkout API.
 */
async function claimFree(item: Wallpaper): Promise<'claimed' | 'auth' | 'legacy'> {
  if (!supabase) return 'legacy';
  const session = await supabase.auth.getSession().catch(() => null);
  const token = session?.data.session?.access_token;
  let response: Response;
  try {
    response = await fetch('/api/claim', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ artworkId: item.id }),
    });
  } catch {
    return 'legacy';
  }
  if ([404, 405, 501].includes(response.status)) return 'legacy';
  if (response.status === 401) return 'auth';
  if (response.ok) return 'claimed';
  const payload = (await response.json().catch(() => null)) as { error?: string } | null;
  if (payload?.error === 'ALREADY_OWNED') return 'claimed';
  throw new Error(
    payload?.error === 'EMAIL_NOT_VERIFIED'
      ? '请先验证邮箱再领取。'
      : '暂时无法领取这幅作品，请稍后重试。',
  );
}

export async function downloadWallpaper(item: Wallpaper, format: DownloadFormat) {
  const revision = getAccountRevision();
  let addedToLibrary = false;
  let unlocked = item.owned;
  if (item.price === 0 && !unlocked) {
    const claim = await claimFree(item);
    if (claim === 'legacy') {
      const result = await api<{ unlocked: boolean }>('/api/checkout', {
        method: 'POST',
        body: JSON.stringify({ id: item.id }),
      });
      if (!result.unlocked) throw new Error('暂时无法添加这幅作品，请稍后重试。');
      addedToLibrary = true;
      unlocked = true;
    } else if (claim === 'claimed') {
      addedToLibrary = true;
      unlocked = true;
    } else {
      throw new Error('免费作品需要登录后领取，请先登录。');
    }
  }
  if (
    (unlocked || SUPABASE_CHECKOUT === 'sandbox') &&
    (await downloadFromSupabase(item, format)) === 'done'
  )
    return { addedToLibrary };
  const response = await fetch(`/api/download/${item.id}/${format}`, {
    credentials: 'same-origin',
    headers: accountHeaders(),
  });
  if (!response.ok) await throwResponseError(response, revision);
  const blob = await response.blob();
  assertAccountRevision(revision);
  saveBlob(blob, item, format);
  return { addedToLibrary };
}
